import React, { useEffect, useMemo, useState } from "react";
import { hotelApi } from "./hotelApi";

export default function HotelSuppliesReplenish({ onBack }) {
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const [supplies, setSupplies] = useState([]);
  const [selectedSupplyId, setSelectedSupplyId] = useState("");
  const [qty, setQty] = useState("");
  const [costPrice, setCostPrice] = useState("");
  const [note, setNote] = useState("");

  const showMsg = (m) => {
    setMsg(m);
    clearTimeout(showMsg._t);
    showMsg._t = setTimeout(() => setMsg(""), 4500);
  };

  const selectedSupply = useMemo(() => {
    return supplies.find((s) => Number(s.id) === Number(selectedSupplyId)) || null;
  }, [supplies, selectedSupplyId]);

  const loadSupplies = async () => {
    const res = await hotelApi.get("/supplies");
    setSupplies(Array.isArray(res.data?.rows) ? res.data.rows : []);
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await loadSupplies();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to load supplies.")
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
  }, []);

  const resetForm = () => {
    setSelectedSupplyId("");
    setQty("");
    setCostPrice("");
    setNote("");
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!selectedSupplyId) {
      showMsg("⚠️ Select a supply item.");
      return;
    }

    if (Number(qty || 0) <= 0) {
      showMsg("⚠️ Quantity must be greater than zero.");
      return;
    }

    try {
      setLoading(true);

      const res = await hotelApi.post("/supplies/replenish", {
        supply_id: Number(selectedSupplyId),
        qty: Number(qty),
        cost_price: Number(costPrice || 0),
        note: note.trim(),
      });

      if (res.data?.success === false) {
        showMsg(
          "❌ " + (res.data?.error || res.data?.message || "Replenishment failed")
        );
        return;
      }

      showMsg("✅ Supply replenished successfully");
      resetForm();
      await refreshAll();
    } catch (e) {
      console.error(e);
      showMsg(
        "❌ " +
          (e?.response?.data?.message ||
            e?.response?.data?.error ||
            "Failed to replenish supply.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <h2 style={{ margin: 0 }}>📦 Replenish Hotel Supplies</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.btn} onClick={refreshAll} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button style={styles.btn2} onClick={onBack}>
            Back
          </button>
        </div>
      </div>

      {msg ? <div style={styles.msg}>{msg}</div> : null}

      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Replenishment Form</div>

          <form style={styles.form} onSubmit={handleSave}>
            <div>
              <label style={styles.label}>Supply Item</label>
              <select
                style={styles.input}
                value={selectedSupplyId}
                onChange={(e) => setSelectedSupplyId(e.target.value)}
                disabled={loading}
              >
                <option value="">Select supply item...</option>
                {supplies.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.item_name} - Stock {Number(s.qty_on_hand || 0).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>

            {selectedSupply && (
              <div style={styles.summaryBox}>
                <div><b>Item:</b> {selectedSupply.item_name}</div>
                <div><b>Category:</b> {selectedSupply.category || "—"}</div>
                <div><b>Current Stock:</b> {Number(selectedSupply.qty_on_hand || 0).toFixed(2)}</div>
                <div><b>Selling Price:</b> {Number(selectedSupply.unit_price || 0).toFixed(2)}</div>
              </div>
            )}

            <div>
              <label style={styles.label}>Quantity to Add</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                style={styles.input}
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label style={styles.label}>Cost Price</label>
              <input
                type="number"
                min="0"
                step="0.01"
                style={styles.input}
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label style={styles.label}>Note</label>
              <textarea
                rows={4}
                style={styles.textarea}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                disabled={loading}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                type="button"
                style={styles.secondaryBtn}
                onClick={resetForm}
                disabled={loading}
              >
                Clear
              </button>
              <button type="submit" style={styles.primary} disabled={loading}>
                {loading ? "Saving..." : "Save Replenishment"}
              </button>
            </div>
          </form>
        </div>

        <div style={styles.card}>
          <div style={styles.cardTitle}>Available Supplies</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Item</th>
                  <th style={styles.th}>Category</th>
                  <th style={styles.th}>Price</th>
                  <th style={styles.th}>Stock</th>
                </tr>
              </thead>
              <tbody>
                {supplies.length === 0 ? (
                  <tr>
                    <td style={styles.td} colSpan={4}>No supplies found.</td>
                  </tr>
                ) : (
                  supplies.map((s) => (
                    <tr key={s.id}>
                      <td style={styles.td}>{s.item_name}</td>
                      <td style={styles.td}>{s.category || "—"}</td>
                      <td style={styles.td}>{Number(s.unit_price || 0).toFixed(2)}</td>
                      <td style={styles.td}>{Number(s.qty_on_hand || 0).toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    padding: 12,
    background: "#f8fafc",
    minHeight: "100vh",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
    flexWrap: "wrap",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    alignItems: "start",
  },
  card: {
    background: "#fff",
    border: "1px solid rgba(15,23,42,.12)",
    borderRadius: 16,
    padding: 12,
    boxShadow: "0 10px 28px rgba(15,23,42,.05)",
  },
  cardTitle: {
    fontWeight: 900,
    marginBottom: 10,
    color: "#0f172a",
    fontSize: 18,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  label: {
    display: "block",
    marginBottom: 6,
    fontWeight: 800,
    color: "#0f172a",
  },
  input: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
  },
  textarea: {
    width: "100%",
    padding: 10,
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.18)",
    background: "#fff",
    outline: "none",
    resize: "vertical",
  },
  summaryBox: {
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: 12,
    lineHeight: 1.8,
  },
  primary: {
    padding: "11px 14px",
    borderRadius: 12,
    border: "none",
    background: "#0f766e",
    color: "#fff",
    fontWeight: 900,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#fff",
    fontWeight: 800,
    cursor: "pointer",
  },
  btn2: {
    padding: "10px 12px",
    borderRadius: 12,
    border: "1px solid rgba(15,23,42,.12)",
    background: "#f1f5f9",
    fontWeight: 800,
    cursor: "pointer",
  },
  msg: {
    marginBottom: 12,
    padding: 10,
    borderRadius: 12,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    color: "#1d4ed8",
    fontWeight: 700,
  },
  tableWrap: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    textAlign: "left",
    fontSize: 12,
    color: "#475569",
    borderBottom: "1px solid rgba(15,23,42,.12)",
    padding: "9px 8px",
    whiteSpace: "nowrap",
    background: "#f8fafc",
  },
  td: {
    padding: "10px 8px",
    borderBottom: "1px solid rgba(15,23,42,.08)",
    fontSize: 13,
    whiteSpace: "nowrap",
  },
};